/**
 * Replays a pointer gesture as an ordinary compiled step.
 *
 * The canvas drives a stroke as `history.begin(command)` (execute: the first
 * point), `history.updateLive(command.append(point))` for every later sample
 * and `history.commitLive()` (seal). This step makes exactly those calls on
 * the same native `LiveCommand`, in the same order, without the live
 * machinery, so the result is byte-identical to the UI stroke and it runs
 * inside a `TransactionCommand` like any other step (one entry, one
 * revision, rollback, undo and redo through the native command).
 *
 * Outcomes mirror `HistoryManager`: a first execute that is not `changed`
 * ends the gesture before it starts (as `begin` does, later points are never
 * applied); a `no-op` seal leaves the document as the gesture left it (as
 * `commitLive` does); a `rejected` seal undoes the stroke.
 */
import { changed, type Command, type CommandExecution, type LiveCommand } from '../../core/commands';
import { EMPTY_DAMAGE, type DamageSet } from '../../core/damage';
import type { EditorDocument } from '../../core/model';

export class LiveReplayCommand<C extends LiveCommand, P> implements Command {
  private live: C | null = null;

  constructor(
    private readonly create: (document: EditorDocument) => C,
    private readonly points: readonly P[],
    private readonly append: (command: C, point: P, document: EditorDocument) => void,
  ) {}

  get label(): string { return this.live?.label ?? 'Stroke'; }

  /** The native command, once executed (tests and journaling). */
  get command(): C | null { return this.live; }

  retainedBytes(): number { return 64 + (this.live?.retainedBytes() ?? 0); }

  execute(document: EditorDocument): CommandExecution {
    if (this.live) {
      this.redo(document);
      return changed(this.damage());
    }
    const command = this.create(document);
    const begun = command.execute(document);
    if (begun.outcome !== 'changed') {
      command.dispose?.();
      return begun;
    }
    for (const point of this.points) this.append(command, point, document);
    const sealed = command.seal(document);
    if (sealed.outcome !== 'changed') {
      if (sealed.outcome === 'rejected') command.undo(document);
      command.dispose?.();
      return sealed;
    }
    this.live = command;
    return sealed.damage === EMPTY_DAMAGE && command.damage ? changed(command.damage()) : sealed;
  }

  undo(document: EditorDocument): void { this.live?.undo(document); }

  redo(document: EditorDocument): void {
    if (!this.live) return;
    if (this.live.redo) this.live.redo(document);
    else this.live.execute(document);
  }

  damage(): DamageSet { return this.live?.damage?.() ?? EMPTY_DAMAGE; }

  toActionStep(): null { return null; }

  dispose(): void { this.live?.dispose?.(); }
}
